import { TextField } from '@radix-ui/themes';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonTextHint } from 'components/common/form/text-hint';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { ITextInput, RegexFilter } from 'interfaces/forms/text';
import { safeFloat, safeNumber } from 'lib_ts/classes/math.utilities';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const DEFAULT_TEXT_DEBOUNCE_MS = 500;

/**
 * Common text input component with optional debouncing.
 *
 * When debouncing is disabled, this works like a normal controlled text input (onChange + props.value).
 * When debouncing is enabled, we use a locally scoped state to handle what text is shown in the input, and only
 * calls the onChangeCallback after the specified delay.
 */
export const CommonTextInput = (props: ITextInput) => {
  const regex = useMemo(() => {
    switch (props.regex) {
      case RegexFilter.Alpha: {
        return /[^a-zA-Z]/g;
      }

      case RegexFilter.AlphaNumeric: {
        return /\W/g;
      }

      case RegexFilter.Numeric: {
        return /[^\d]/g;
      }

      case RegexFilter.Whitespace: {
        return /\s/g;
      }

      default: {
        return undefined;
      }
    }
  }, [props.regex]);

  const debounceEnabled = props.debounceMs && props.debounceMs > 0;

  // locally scoped state that's only really used when debouncing is enabled
  const [value, setValue] = useState(props.value);

  const onChangeCallback = useCallback(
    (v: string | undefined) => {
      if (props.onChange) {
        props.onChange(v);
      }

      if (props.onNumericChange) {
        if (!v) {
          props.onNumericChange(0);
        } else {
          const maybeNum = safeNumber(v);
          if (maybeNum !== undefined) {
            props.onNumericChange(maybeNum);
          }
        }
      }

      if (props.onFloatChange) {
        const maybeFloat = safeFloat(v);
        props.onFloatChange(maybeFloat ?? '');
      }

      if (props.onOptionalNumericChange) {
        const maybeNum = safeNumber(v);
        props.onOptionalNumericChange(maybeNum);
      }
    },
    [props.onChange, props.onNumericChange, props.onOptionalNumericChange]
  );

  const latestOnChange = useLatest(onChangeCallback);

  const tryDebounce = useCallback(
    debounce(latestOnChange.current, props.debounceMs ?? 0),
    [props.debounceMs]
  );

  // if parent changes value => update local value
  useEffect(() => {
    setValue((prev) => {
      if (prev === props.value) {
        return prev;
      }
      return props.value;
    });
  }, [props.value]);

  // if user changes value using the input => debounce to parent
  useEffect(() => {
    if (!debounceEnabled) {
      return;
    }

    tryDebounce(value);
  }, [value, tryDebounce, debounceEnabled]);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <TextField.Root
        size={props.size}
        autoComplete={props.autoComplete}
        className={StringHelper.classNames([
          props.className,
          props.monospace ? 'font-mono' : undefined,
        ])}
        // radix does not display the inputColor until the input is focused, so we'll force it to show here
        style={
          props.inputColor
            ? {
                outlineOffset: '-1px',
                outline: '2px solid var(--text-field-focus-color)',
              }
            : undefined
        }
        color={props.inputColor}
        disabled={props.disabled}
        maxLength={props.maxLength}
        name={props.name}
        onChange={(e) => {
          const v = regex ? e.target.value.replace(regex, '') : e.target.value;

          if (!debounceEnabled) {
            onChangeCallback(v);
            return;
          }

          setValue(v);
        }}
        onWheel={(e) => {
          if (props.type === 'number') {
            // prevent scrolling from modifying the number value
            (e.target as HTMLElement).blur();
          }
        }}
        onKeyUp={(e) => {
          props.onKeyUp?.({
            code: e.code,
            ctrl: e.ctrlKey,
            alt: e.altKey,
            shift: e.shiftKey,
          });
        }}
        placeholder={
          props.placeholder ? t(props.placeholder).toString() : undefined
        }
        readOnly={props.readOnly}
        required={!props.optional}
        type={props.type ?? 'text'}
        // only uses locally scoped state when debouncing is enabled
        value={debounceEnabled ? value : props.value}
        autoCapitalize="off"
        autoCorrect="off"
      >
        {props.leftSlot && (
          <TextField.Slot side="left">{props.leftSlot}</TextField.Slot>
        )}
        {props.rightSlot && (
          <TextField.Slot side="right">{props.rightSlot}</TextField.Slot>
        )}
      </TextField.Root>

      <CommonTextHint {...props} />
    </CommonInputWrapper>
  );
};
